import { makeFetchOrThrow } from '@/data/helpers/makeFetch';
import { Method } from '@/data/enums/method';
import { QueryKey } from '@/data/enums/queryKeys';
import { Services } from '@/data/enums/services';
import { toast } from '@liveauctioneers/hammer-ui-core/toast';
import { useAppContext } from '@/providers/AppContextProvider/AppContextProvider';
import { useEffectiveHouseId } from '@/hooks/useEffectiveHouseId/useEffectiveHouseId';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import type {
    EnabledDestinationMarketplacesApiRequest,
    EnabledDestinationMarketplacesResponse,
} from '@/types/house.types';

type PutEnabledDestinationMarketplacesContext = {
    previous?: EnabledDestinationMarketplacesResponse;
};

export const usePutEnabledDestinationMarketplaces = () => {
    const t = useTranslations('AdminToolbar.crossListing');
    const { deployment, token } = useAppContext();
    const houseId = useEffectiveHouseId();
    const queryClient = useQueryClient();
    const queryKey = [QueryKey.EnabledDestinationMarketplaces, houseId];

    return useMutation<
        EnabledDestinationMarketplacesResponse,
        Error,
        EnabledDestinationMarketplacesApiRequest,
        PutEnabledDestinationMarketplacesContext
    >({
        mutationFn: (body) => {
            if (!houseId) {
                throw new Error('No house ID found. Please contact your administrator.');
            }
            if (!token) {
                throw new Error('No token found. Please contact your administrator.');
            }

            return makeFetchOrThrow<EnabledDestinationMarketplacesResponse>({
                apiPath: `houses/${houseId}/enabled-destination-marketplaces`,
                apiService: Services.SellerPortal,
                body,
                deployment,
                method: Method.Put,
                token,
            });
        },
        mutationKey: [QueryKey.EnabledDestinationMarketplaces, houseId, 'update'],
        onError: (_error, _variables, context) => {
            if (context?.previous) {
                queryClient.setQueryData(queryKey, context.previous);
            }
            toast('error', {
                description: t('toastError'),
                title: t('toastErrorTitle'),
            });
        },
        onMutate: async (request) => {
            await queryClient.cancelQueries({ queryKey });
            const previous = queryClient.getQueryData<EnabledDestinationMarketplacesResponse>(queryKey);

            queryClient.setQueryData<EnabledDestinationMarketplacesResponse>(queryKey, request);

            return { previous };
        },
        onSettled: () => {
            void queryClient.invalidateQueries({ queryKey });
            void queryClient.invalidateQueries({ queryKey: [QueryKey.House, houseId] });
        },
        onSuccess: (response) => {
            queryClient.setQueryData(queryKey, response);
        },
    });
};
